import { Button, InlineNotice } from "@bafe/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { api } from "../../lib/api";

export function VerifyEmail() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const verification = useQuery({
		queryKey: ["verify-email", token],
		// The endpoint answers 204, and a query may not resolve to undefined.
		queryFn: async () => {
			await api.verifyEmail(token ?? "");
			return true as const;
		},
		enabled: token !== null,
		retry: false,
	});

	const resend = useMutation({ mutationFn: () => api.resendVerification() });

	if (token === null) {
		return (
			<AuthShell
				title="Confirm your email address"
				description="We sent you a link. Open it on this device to finish confirming."
				footer={
					<Link to="/" className="font-medium text-(--color-brand-700) underline underline-offset-2">
						Continue to Key
					</Link>
				}
			>
				<div className="flex flex-col gap-4">
					<p className="text-sm text-(--text-secondary)">
						If it has not arrived in a few minutes, check your spam folder, then send it again.
					</p>

					{resend.isSuccess && (
						<InlineNotice tone="positive">We sent another link just now.</InlineNotice>
					)}
					{resend.isError && <ErrorPanel error={resend.error} />}

					<Button
						variant="secondary"
						fullWidth
						pending={resend.isPending}
						pendingLabel="Sending…"
						onClick={() => resend.mutate()}
					>
						Send the link again
					</Button>
				</div>
			</AuthShell>
		);
	}

	return (
		<AuthShell title="Confirming your email address">
			{verification.isPending && (
				<p className="text-sm text-(--text-secondary)" role="status">
					Checking your link…
				</p>
			)}

			{verification.isError && (
				<div className="flex flex-col gap-4">
					<ErrorPanel
						error={verification.error}
						title="We could not confirm this link."
						onRetry={() => void verification.refetch()}
						retryPending={verification.isFetching}
					/>
					<Button
						variant="secondary"
						fullWidth
						pending={resend.isPending}
						pendingLabel="Sending…"
						onClick={() => resend.mutate()}
					>
						Send a new link
					</Button>
				</div>
			)}

			{verification.isSuccess && (
				<div className="flex flex-col gap-4">
					<InlineNotice tone="positive" title="Email confirmed">
						Account recovery and service notices will reach you.
					</InlineNotice>
					<Button variant="primary" fullWidth onClick={() => window.location.assign(import.meta.env.BASE_URL)}>
						Continue to Key
					</Button>
				</div>
			)}
		</AuthShell>
	);
}
